/**
 * The master dashboard: the owner's "tracking tower", and the app's landing view.
 *
 * The order of what follows is his, and it is contract rather than layout taste:
 * what do I need to do, what am I waiting on, key updates, what I have. Intent
 * sits above all four because nothing below it can be ranked without it. A
 * surface that renders the five as equals is exactly the flat wall he said was
 * "very hard to follow", so the priority is carried by size, weight and position
 * (see the .mo-rank-N sizes in mo.css) and not by colour, which he would have to
 * learn before it meant anything.
 *
 * The ask-and-discover field is deliberately NOT ranked with the four. It is not
 * part of the payload (see packages/shared/src/dashboard.ts) and it is an
 * affordance rather than a layer, so it sits quiet in the header where it is
 * always in reach without competing with what he owes.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type {
  Dashboard,
  DashboardCommitment,
  DashboardKeyAction,
  DashboardLanes,
  DashboardTelemetry,
  DashboardGap,
  DashboardHoldings,
  DashboardMeeting,
  DashboardObjective,
  DashboardProposal,
  DashboardUpdate,
} from '@rbv/shared';
import { BUILD_ID } from '../build.js';
import { DailyBrief } from './DailyBrief.js';
import { NewsTicker } from './NewsTicker.js';
import { AnnunciatorStrip } from './Annunciators.js';
import { CommitmentReview } from './CommitmentReview.js';
import { HealthPanel } from './Health.js';
import { ChangesPanel } from './Changes.js';
import {
  closeCommitment,
  dueInDaysLabel,
  fetchDashboard,
  fetchLane,
  resolveAttention,
  type Loaded,
} from './api.js';
import { ObjectiveForm } from './ObjectiveForm.js';
import { useApp } from '../store.js';
import { usePaneState } from '../views/paneState.js';
import { vault } from '../sync/vault.js';
import './mo.css';
import './deck.css';
import { Icon, type IconName } from './Icon.js';
import { Brain } from './Brain.js';
import { StackedBar } from './Chart.js';
import { MergePanel } from './MergePanel.js';
import { SocialPanel } from './SocialPanel.js';
import { ChatLauncher, openAsk } from './ChatOverlay.js';
import { CommandDeck, ViewPicker, isLive, type CrewRow } from './CommandDeck.js';
import { LiveWall } from './LiveWall.js';
import { WorkBoard } from './WorkBoard.js';
import { readView, writeView, type DeckView } from './views.js';
import { IDLE_MS, useIdle } from './idle.js';
import { PipelinePanel } from './Pipeline.js';
import { WeeklyOpsPanel } from './WeeklyOps.js';
import { UntouchedPanel } from './Untouched.js';

export function MoDashboard(): JSX.Element {
  const [data, setData] = useState<Loaded<Dashboard>>({ state: 'loading' });

  const load = useCallback(async () => {
    setData(await fetchDashboard());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onReject = useCallback(
    async (id: number) => {
      await resolveAttention(id);
      await load();
    },
    [load],
  );

  /**
   * Accepting happens in two steps because A5-3 will not let it happen in one: the
   * objective is authored in his own words, and only once it is written does the
   * proposal clear. Resolving first would lose the proposal if the write failed.
   */
  const reveal = useReveal();
  /*
   * The skin lives in the store, not here.
   *
   * It started as local state plus a `<html>` attribute set from this component, which worked
   * exactly as long as the dashboard was mounted: opening a note unmounted it and the notes app
   * reverted to plain. The switch below still lives in the rail, because the moment he wants the
   * plain look is the moment someone walks up to the desk, but the state is app-wide.
   */
  const skin = useApp((s) => s.skin);
  const setSkin = useApp((s) => s.setSkin);

  // Persisted: a rail width is a preference, unlike a panel he collapsed for a glance.
  const [railOpen, setRailOpen] = useState<boolean>(() => {
    try {
      return globalThis.localStorage?.getItem('mo-os:rail') !== 'closed';
    } catch {
      // Private mode can throw on read. A rail state is not worth a blank page.
      return true;
    }
  });
  useEffect(() => {
    try {
      globalThis.localStorage?.setItem('mo-os:rail', railOpen ? 'open' : 'closed');
    } catch {
      // Same reasoning.
    }
  }, [railOpen]);

  /*
   * WHICH VIEW the dashboard is in: the live wall, the work board, the Command Deck or this tower.
   *
   * The key, the default and the list live in views.ts. Two rules from the old shape switch still
   * hold and are why he once reported four deploys in a row as "still the old platform": the
   * preference is written ONLY when he presses a switch, never from an effect on mount, and the
   * key is a fresh one so stamped defaults from an earlier version cannot pose as choices.
   */
  const [shape, setShape] = useState<DeckView>(() =>
    readView(globalThis.localStorage, globalThis.innerWidth ?? 0),
  );

  /** Switch view AND record it, because this is the only place a choice is actually made. */
  const chooseShape = useCallback((next: DeckView) => {
    setShape(next);
    writeView(globalThis.localStorage, next);
  }, []);

  /*
   * The wall as a screensaver, on every view but itself. After ten idle minutes it overlays the
   * board; the first input dismisses it and lands nowhere else, so his place is kept. `woke` covers
   * the one render between the waking input and the idle hook noticing it.
   */
  const idle = useIdle(IDLE_MS, shape !== 'live');
  const [woke, setWoke] = useState(false);
  useEffect(() => {
    if (!idle) setWoke(false);
  }, [idle]);

  const onAccepted = useCallback(
    async (id: number) => {
      await resolveAttention(id);
      await load();
    },
    [load],
  );

  if (data.state === 'loading') {
    return (
      <main className="mo-dash">
        <p className="mo-dash-note">Loading.</p>
      </main>
    );
  }

  if (data.state === 'disabled') {
    return (
      <main className="mo-dash">
        <h1 className="mo-dash-title">Tracking tower</h1>
        <p className="mo-dash-note">{data.message}</p>
        <VaultLink />
      </main>
    );
  }

  if (data.state === 'error') {
    return (
      <main className="mo-dash">
        <h1 className="mo-dash-title">Tracking tower</h1>
        <p className="mo-dash-note">Could not load the dashboard: {data.message}</p>
        <VaultLink />
      </main>
    );
  }

  const d = data.value;

  /*
   * Placed AFTER the loading, disabled and error branches on purpose: those three states are
   * about whether there is a dashboard at all, and both shapes would render them identically.
   * Duplicating them into the deck would mean two places to fix the next time an error message
   * needs changing.
   */
  if (shape === 'live') {
    return <LiveWall dashboard={d} onPick={chooseShape} />;
  }
  const saver =
    idle && !woke ? <LiveWall dashboard={d} onPick={chooseShape} screensaver onWake={() => setWoke(true)} /> : null;
  if (shape === 'work') {
    return (
      <>
        {saver}
        <WorkBoard dashboard={d} onPick={chooseShape} onReload={() => void load()} />
      </>
    );
  }
  if (shape === 'deck') {
    return (
      <>
        {saver}
        <CommandDeck
          dashboard={d}
          railOpen={railOpen}
          onToggleRail={() => setRailOpen(!railOpen)}
          onReload={() => void load()}
          onResolve={onAccepted}
          onPickView={chooseShape}
        />
      </>
    );
  }

  return (
    <main
      className="mo-dash deck"
      data-rail={railOpen ? 'open' : 'closed'}
    >
      {saver}
      <Rail
        open={railOpen}
        onToggle={() => setRailOpen(!railOpen)}
        onPick={reveal}
        subs={railSubs(d)}
        skin={skin}
        onToggleSkin={() => setSkin(skin === 'beskar' ? 'plain' : 'beskar')}
      />

      <header className="deck-head">
        <div className="deck-id">
          <h1 className="deck-greeting">{greeting()}</h1>
          <p className="deck-sub">
            {longDate(d.generatedAt)}
            <span className="deck-when">
              <span className="deck-live" aria-hidden="true" />
              {whenLabel(d.generatedAt)}
            </span>
          </p>
          {/*
            Under the greeting, before anything countable. It is the only thing on the board that
            answers "does anything need me" without him reading a number, which is how he actually
            uses this at 6am.
          */}
          <AnnunciatorStrip dashboard={d} />
        </div>
        {/* Fixed, and it stays fixed: the one element in the header that is not a readout. */}
        <p className="deck-motto">
          <b>Clarity comes from signal.</b>
          <span>Focus. Decide. Move.</span>
        </p>

        {/* One container, not two placements in the same cell. */}
        <div className="deck-askrow">
          <AskField />
          {/* Ask the Brain and Ask Zou4Mo open as an overlay. They used to sit in a dashboard
              column, where a 25-second answer and a several-hundred-word draft competed for
              height with panels answering a different question. See ChatOverlay.tsx. */}
          <ChatLauncher />
        </div>
        {/*
          The ingestion verdict, in the corner where his mockup has one.

          It is DERIVED, not decorative: it goes amber the moment a limitation is recorded,
          which is the same list the footer prints in full. A permanently green light would
          be worse than none, because it is the one element on the board claiming that
          everything else on the board can be trusted.
        */}
        {/* Both in one container. Placed individually they were given the same grid cell and
            stacked, which hid the switch under the light. */}
        <div className="deck-headright">
          <SystemState limitations={d.limitations} />
          {/* The way across to the other surfaces. In the header rather than the rail because the
              rail retracts, and a switch he cannot find is a switch he does not have. The same
              picker the deck uses, so the three views are named identically wherever you ask. */}
          <ViewPicker current="tower" onPick={chooseShape} />
        </div>
      </header>

      <Hero dashboard={d} onPick={reveal} />

      {/*
        TIER 1, DECIDE. Where he is the bottleneck, and nothing else.

        Four panels, each hard-capped, each stating what it is not showing. This column exists to
        answer one question at 6am: is anything stuck on me. A panel that belongs here has to fail
        the test "could this wait until he goes looking for it", and everything that passes that
        test is in Tier 3.
      */}
      <div className="deck-col deck-col-left" aria-labelledby="tier-decide">
        <p className="deck-tier" id="tier-decide">
          Decide
        </p>
        <Panel
          title="Key actions"
          icon="action"
          hue="action"
          count={d.keyActions.length}
          weight="mo-rank-1"
          note={d.keyActionsMore > 0 ? `${d.keyActionsMore} more, ${d.lanes.archive} archived` : undefined}
        >
          <KeyActionList items={d.keyActions} />
        </Panel>
        {/*
          Moved out of the far-right column, where a question waiting on his judgement sat below
          five panels he does not need to read. It is a decision only he can make, so it belongs in
          the tier named for that.
        */}
        <ProposalsBlock proposals={d.proposals} more={d.proposalsMore} onReject={onReject} onAccepted={onAccepted} />
        <Panel title="What I need to do" icon="owed" hue="action" count={d.owedTotal} rank="owed" weight="mo-rank-1">
          <CommitmentList
            items={d.owed}
            total={d.owedTotal}
            lane="owed"
            empty="Nothing is owed by you right now."
            onClosed={() => void load()}
          />
        </Panel>
        {/*
          Stuck work sits in Decide because a thing that is BLOCKED is, by definition, waiting on a
          decision or a nudge from him. `blocked_on` has been populated since A2 and nothing has
          ever read it, so this panel is the first surface to ask what is not moving and why.
        */}
        <Panel
          title="Stuck"
          icon="waiting"
          hue="waiting"
          count={d.stuckTotal}
          weight="mo-rank-2"
          note={d.stuckTotal > d.stuck.length ? `${d.stuckTotal - d.stuck.length} more not shown` : undefined}
        >
          <StuckList items={d.stuck} />
        </Panel>
        <Panel title="Waiting on others" icon="waiting" hue="waiting" count={d.waitingTotal} rank="waiting" weight="mo-rank-2">
          <CommitmentList
            items={d.waiting}
            total={d.waitingTotal}
            lane="waiting"
            empty="Nothing is outstanding from anyone else."
            onClosed={() => void load()}
          />
        </Panel>
      </div>

      {/*
        TIER 2, ACCOUNTS, in the widest column.

        Relationships rather than individual commitments: Tier 1 is what he personally owes, this
        is which account is waiting on a move. The ask sits under it because most of what he asks
        this thing is about one of these accounts, and it is the only column with the width for a
        drafted proposal to be readable.
      */}
      <div className="deck-col deck-col-mid" aria-labelledby="tier-accounts">
        <p className="deck-tier" id="tier-accounts">
          Accounts
        </p>
        {/* The full chase queue rides inside the panel, so the tower and the deck cannot
            disagree about who is owed a move. */}
        <PipelinePanel accounts={d.pipeline} chases={d.chases} onReload={() => void load()} />
        {/* Beside Thin on contacts, because they are two halves of the same question: how many
            people hold each relationship, and who else is already reachable. */}
        <UntouchedPanel />
        <WeeklyOpsPanel />

        <section className="deck-panel" data-hue="action">
          <header className="deck-panel-head">
            <Icon name="action" size={16} className="deck-panel-icon" />
            <h2 className="deck-panel-title">Commitment load</h2>
            <span className="deck-count">{d.lanes.due + d.lanes.live}</span>
          </header>
          <StackedBar
            slices={[
              { label: 'Due', value: d.lanes.due, token: '--accent' },
              { label: 'Live', value: d.lanes.live, token: '--success' },
              { label: 'Scheduled', value: d.lanes.scheduled, token: '--warning' },
              { label: 'Overdue', value: d.lanes.overdue, token: '--danger' },
              { label: 'Archived', value: d.lanes.archive, token: '--border-strong' },
            ]}
          />
        </section>
      </div>

      {/*
        TIER 3, CONTEXT. True, useful, and none of it needs him right now.

        WHAT WAS DROPPED FROM THE BOARD ENTIRELY, at his decision: the knowledge graph, the node
        mix donut and the "What I have" holdings block. All three were counts of how much the vault
        holds, and a note count is a fact about the system rather than about his work: it moves
        every day, it never asks anything of him, and it took the space three panels that do. The
        honest coverage figures survive in Telemetry, which is where "can I rely on this" lives.
        The Brain stays because its spokes are ingestion LANES going quiet, which is a real signal.
      */}
      <div className="deck-col deck-col-right" aria-labelledby="tier-context">
        <p className="deck-tier" id="tier-context">
          Context
        </p>
        {/*
          The next meeting, lifted OUT of the Today list and given the top of the column.

          It was the first row of a list that also held every change to the vault, so the one
          item with a deadline attached looked exactly like a note that got retitled. It now
          leads, with the rest of the run under it and a Prepare on each, because the meeting he
          needs to think about first is often not the next one.
        */}
        <NextMeeting meetings={d.agenda} />

        <Panel
          title="Today"
          icon="agenda"
          hue="time"
          /* Meetings plus changes: the panel answers both questions, so the count covers both. */
          count={d.agenda.length + d.updates.length}
          rank="updates"
          weight="mo-rank-3"
        >
          <AgendaList meetings={d.agenda} />
          <UpdateList updates={d.updates} />
        </Panel>

        {/*
          Renders nothing at all until Hermes has pushed something, so it costs no space while
          unwired.
        */}
        {d.daily !== undefined && (
          <Panel title="Today so far" icon="agenda" hue="time" weight="mo-rank-2">
            <DailyBrief daily={d.daily} />
          </Panel>
        )}

        <Brain
          total={d.holdings.totalNotes}
          inputs={[
            { label: 'Mail', count: d.telemetry.attentionByKind['commitment-due'] ?? 0, icon: 'inbox', target: '' },
            { label: 'Calendar', count: d.telemetry.meetings, icon: 'agenda', target: 'meeting' },
            { label: 'Meetings', count: d.telemetry.meetingsTranscribed, icon: 'clock', target: '' },
            { label: 'Vault', count: d.holdings.counts.person ?? 0, icon: 'notes', target: 'person' },
          ]}
          outputs={[
            { label: 'Actions', count: d.lanes.due + d.lanes.live, icon: 'action', target: 'commitment' },
            { label: 'Updates', count: d.updates.length, icon: 'update', target: '' },
            { label: 'Objectives', count: d.intent.length, icon: 'objective', target: 'objective' },
            { label: 'Questions', count: d.proposals.length, icon: 'judgement', target: '' },
          ]}
          onPick={(t) => {
            if (t !== '') reveal(t);
          }}
        />

        <AgentsPanel crew={d.telemetry.crew} />
        {/* Under Agents on purpose: both answer "what is acting for me, and does it need
            me?" The queue is the one panel on this board with an irreversible button, so
            it sits where he already looks for automation rather than beside his own
            commitments. */}
        <SocialPanel />
        <IntentBlock objectives={d.intent} onSaved={() => void load()} />
        <GapsBlock gaps={d.gaps} />
        {/* Directly above Telemetry, because both answer "can I rely on what this thing tells me":
            Telemetry says whether the pollers ran, this says whether the notes they wrote hold up. */}
        <HealthPanel rows={d.health ?? []} />
        {/* Under the health panel: that one says what the brain is unsure of, this one says what it
            did about things while he was not looking. Both answer "can I rely on this". */}
        <ChangesPanel />
        <Telemetry telemetry={d.telemetry} lanes={d.lanes} holdings={d.holdings} />
      </div>

      {/* Wrapped, so these sit in the footer row rather than auto-placing into a
          content cell and displacing the columns, which is what broke the layout. */}
      {/*
        The crawl spans the foot of the board, above the footer links.
        
        Its own row rather than inside `.deck-foot`: it needs the full width, and the footer is a
        flex row of small controls that would squeeze it. Renders nothing until Hermes has pushed
        headlines, so it costs no space while unwired.
      */}
      {d.daily?.news != null && d.daily.news.items.length > 0 && (
        <NewsTicker items={d.daily.news.items} at={d.daily.news.at} />
      )}

      <div className="deck-foot">
        <VaultLink />
        <LimitationsBlock limitations={d.limitations} />
        {/*
          Which build this browser is running. Quiet and always present, because the case it
          exists for is a screenshot: a service worker can pin a tab to an old bundle, and when
          it does, "the fix did not work" and "you are looking at last week's code" look
          identical. Three fixes were reported as shipped while his phone was on the previous
          build. Now the screenshot answers it.
        */}
        <p className="deck-build" title="The build running in this browser">
          build {BUILD_ID}
        </p>
      </div>
    </main>
  );
}

/**
 * The left rail: expanded with labels, or retracted to icons only.
 *
 * He asked for it "expandable or retractable to hidden menu". Retracted keeps the
 * ICONS rather than hiding the rail entirely, because a rail that vanishes leaves no
 * affordance to bring it back, and a 52px icon column is still a real target on a
 * phone. Items drive the same search reveal the rest of the deck uses, so nothing here
 * pretends to route somewhere that does not exist yet.
 */
function Rail({
  open,
  onToggle,
  onPick,
  subs,
  skin,
  onToggleSkin,
}: {
  open: boolean;
  onToggle: () => void;
  onPick: (t: string) => void;
  skin: 'beskar' | 'plain';
  onToggleSkin: () => void;
  /**
   * Per-item sublabels. The mock captions every nav row, and the captions are the
   * reason the rail is worth expanding: "196 to answer" tells him where to go, where
   * "Judgement" only tells him the row exists. Every one is COUNTED from the payload,
   * never written in, so the rail cannot drift from the panels it points at.
   *
   * Null means the row has no honest number yet, and the caption is then omitted
   * rather than filled with a placeholder.
   */
  subs: Partial<Record<string, string | null>>;
}): JSX.Element {
  const items: { icon: IconName; label: string; target: string }[] = [
    { icon: 'action', label: 'Key actions', target: 'commitment' },
    { icon: 'agenda', label: 'Calendar', target: 'meeting' },
    { icon: 'objective', label: 'Objectives', target: 'objective' },
    { icon: 'judgement', label: 'Judgement', target: '' },
    { icon: 'graph', label: 'Knowledge', target: '' },
    { icon: 'agent', label: 'Agents', target: '' },
    { icon: 'notes', label: 'Notes', target: '' },
  ];
  return (
    <nav className="deck-rail" aria-label="Deck sections">
      {/*
        The brand block. Retracted it collapses to the mark alone: a wordmark that
        wraps or clips in a 52px column looks broken, and the mark still identifies
        the rail.
      */}
      <div className="deck-brand">
        <span className="deck-brand-mark" aria-hidden="true">
          MO
        </span>
        <span className="deck-brand-text">
          <span className="deck-brand-name">MO OS</span>
          <span className="deck-brand-sub">Mind Operating System</span>
        </span>
      </div>

      <button
        type="button"
        className="deck-rail-toggle"
        aria-expanded={open}
        title={open ? 'Retract the menu' : 'Expand the menu'}
        onClick={onToggle}
      >
        <Icon name="chevron" size={16} className="deck-rail-chevron" />
        <span className="deck-rail-label">Menu</span>
      </button>
      <ul className="deck-rail-list">
        {items.map((it) => {
          const sub = subs[it.label];
          return (
            <li key={it.label}>
              <button
                type="button"
                className="deck-rail-item"
                // Retracted on a desktop the label is hidden, so the tooltip carries
                // the count. On a phone the label is visible and this is redundant but
                // harmless, and a hover title is unreachable there anyway.
                title={sub != null ? `${it.label}: ${sub}` : it.label}
                disabled={it.target === ''}
                onClick={() => it.target !== '' && onPick(it.target)}
              >
                <Icon name={it.icon} size={18} />
                {/*
                  Always rendered, hidden by CSS when the rail is retracted, NOT removed
                  from the DOM. Two reasons. The phone layout turns the rail into a chip
                  strip where labels must show regardless of the retract state, and CSS
                  cannot bring back an element JavaScript declined to render. And a
                  screen reader gets the section name either way, where an icon-only
                  button would announce nothing useful.
                */}
                <span className="deck-rail-label">
                  {it.label}
                  {sub != null && <span className="deck-rail-sub">{sub}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/*
        The skin switch, at the foot of the rail rather than in Settings.
        
        It belongs where it is used: the moment he needs the plain look is the moment someone walks
        up to the desk, and that is not a moment for two clicks through a settings dialog. The label
        says what it will DO rather than what is currently on, which is the difference between a
        switch you can read at a glance and one you have to reason about.
      */}
      <button
        type="button"
        className="deck-skin-toggle"
        onClick={onToggleSkin}
        title={skin === 'beskar' ? 'Switch to the plain instrument look' : 'Switch to the ship console look'}
      >
        {skin === 'beskar' ? 'Plain deck' : 'Ship deck'}
      </button>
    </nav>
  );
}

/**
 * The rail captions, derived from the payload.
 *
 * Pulled out of `Rail` so it can be tested without rendering, and so the one rule
 * that matters is visible in one place: a caption is a COUNT of something the deck
 * already shows. Notes has no caption because "the vault" is a label, not a number,
 * and the node total already captions Knowledge.
 */
export function railSubs(d: Dashboard): Record<string, string | null> {
  const n = (v: number): string => v.toLocaleString();
  return {
    'Key actions': `${n(d.owed.length)} owed, ${n(d.waiting.length)} waiting`,
    Calendar: d.telemetry.meetings === 0 ? 'nothing scheduled' : `${n(d.telemetry.meetings)} meetings`,
    Objectives: d.intent.length === 0 ? 'none set' : `${n(d.intent.length)} tracked`,
    Judgement: d.proposals.length === 0 ? 'nothing to answer' : `${n(d.proposals.length)} to answer`,
    Knowledge: `${n(d.holdings.totalNotes)} nodes`,
    // Hermes is the only wired agent and the panel says so; three more are greyed.
    Agents: '1 live, 3 planned',
    Notes: null,
  };
}

/**
 * Reveal a node from the deck.
 *
 * `openView({kind:'note'})` needs a note ID and the dashboard payload carries vault
 * PATHS, with no resolver on this surface. Rather than invent one, this drives the
 * search pane the Ask field already uses, which finds the note by title and works
 * today. Swap it for a real path lookup when one exists.
 */
export function useReveal(): (pathOrTitle: string) => void {
  const setSearchQuery = usePaneState((st) => st.setSearchQuery);
  const openView = useApp((st) => st.openView);
  const openNote = useApp((st) => st.openNote);
  const setSidebarTab = useApp((st) => st.setSidebarTab);
  return useCallback(
    (pathOrTitle: string) => {
      /*
       * OPEN THE NOTE, and only fall back to search when there is genuinely no note to open.
       *
       * This used to set a search query and then `openView({ kind: 'empty' })` for EVERY row on the
       * deck. `{ kind: 'empty' }` is the `/home` route, so every click on every panel landed on the
       * empty-vault screen, and whether he saw anything at all depended on the notes sidebar
       * happening to be open on the search tab. He reported it as "any link takes you to /home,
       * which goes nowhere", which is exactly right.
       *
       * The old comment said there was "no resolver on this surface". There is:
       * `vault.findByPath` maps a vault-relative path to a note id, and the dashboard carries real
       * paths (`mo/commitments/Send the outline.md`), so most rows can open the actual note.
       */
      if (pathOrTitle.endsWith('.md')) {
        const id = vault.findByPath(pathOrTitle);
        if (id !== null) {
          openNote(id);
          return;
        }
      }

      /*
       * Fallback, for the refs that are NOT vault paths and never will be: a meeting title, and
       * synthetic refs like `mo:identity:a@b.com`. Search is the right answer for those, but it has
       * to be VISIBLE, so the sidebar is opened on the search tab rather than leaving him on a
       * blank page with a query typed into a panel he cannot see. `setSidebarTab` opens the
       * sidebar as well as selecting the tab.
       */
      const title = pathOrTitle.replace(/^.*\//, '').replace(/\.md$/, '');
      setSearchQuery(title);
      setSidebarTab('search');
      openView({ kind: 'empty' });
    },
    [setSearchQuery, setSidebarTab, openView, openNote],
  );
}

/**
 * A panel shell: same head, same affordances, and collapsible.
 *
 * Collapsing is the answer to "shrinking lists". A dashboard this dense is unusable if
 * every panel insists on its full height, and which panels matter changes by the hour.
 * The head is the toggle, `rank` keeps the priority contract testable, and the state is
 * local because a collapsed panel is a glance-time preference, not a setting.
 */
export function Panel({
  title,
  icon,
  count,
  note,
  rank,
  weight,
  hue,
  defaultOpen = true,
  children,
}: {
  title: string;
  icon: IconName;
  count?: number;
  note?: string;
  rank?: string;
  /** Extra class, used for the mo-rank-* visual weights. */
  weight?: string;
  /** Section hue: what KIND of thing this panel holds. See tokens.css. */
  hue?: 'action' | 'waiting' | 'time' | 'know' | 'intent' | 'ask' | 'health';
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      className={weight === undefined ? 'deck-panel' : `deck-panel ${weight}`}
      data-rank={rank}
      data-hue={hue}
      data-open={open}
    >
      <button
        type="button"
        className="deck-panel-head deck-panel-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icon name={icon} size={16} className="deck-panel-icon" />
        <h2 className="deck-panel-title">{title}</h2>
        {count !== undefined && <span className="deck-count">{count}</span>}
        <Icon name="chevron" size={16} className="deck-chevron" />
      </button>
      {open && (
        <div className="deck-panel-body">
          {note !== undefined && <p className="deck-panel-note">{note}</p>}
          {children}
        </div>
      )}
    </section>
  );
}

/** Time-of-day greeting, which is what makes the surface feel addressed to him. */
export function greeting(): string {
  const h = new Date().getHours();
  const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  return `${part}, Mo`;
}

export function longDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/*
 * `FuturePanel` lived here: a drawn, greyed, explicitly-not-connected panel, on his instruction
 * that panels we would build later should be visible rather than absent. It was removed when its
 * last user, the Projects placeholder, was replaced by the real Weekly operations panel. Bring it
 * back from git history if another placeholder is ever wanted; do not reintroduce a greyed panel
 * with invented figures behind it, which is the thing it was designed to prevent.
 */

/**
 * What is blocked, and what it is blocked on.
 *
 * The `blocked_on` text is rendered VERBATIM rather than categorised. It is one short phrase
 * written by the distiller from what someone actually said, and bucketing it into "waiting on
 * client / waiting on legal" would replace a fact with a guess about which bucket it belongs in.
 *
 * Age leads each row because the value is in the tail: blocked since Tuesday is a normal working
 * state, blocked since March is forgotten or dead. Undated says so rather than showing a number.
 */
export function StuckList({ items }: { items: Dashboard['stuck'] }): JSX.Element {
  const reveal = useReveal();
  if (items.length === 0) {
    return <p className="mo-empty">Nothing is recorded as blocked.</p>;
  }
  return (
    <ul className="mo-stuck">
      {items.map((s) => (
        <li key={s.path} className="mo-stuck-row">
          <button type="button" className="mo-stuck-text" onClick={() => reveal(s.path)}>
            {s.text}
          </button>
          <p className="mo-stuck-on">Blocked on: {s.blockedOn}</p>
          <p className="mo-meta">
            <span>{s.ageDays === null ? 'no date on the note' : `${s.ageDays}d`}</span>
            {s.counterparty !== '' && <span>{s.counterparty}</span>}
            {s.topic !== '' && <span>{s.topic}</span>}
          </p>
        </li>
      ))}
    </ul>
  );
}

/*
 * `ContactRiskPanel` lived here and was folded onto the account row on 2026-08-11.
 *
 * It counted contacts from the VAULT while the never-written-to panel counted them from TWENTY,
 * so the board showed two contradictory answers to one question: three accounts rendered as
 * "nobody on record" while the CRM held a contact for all fifteen. He called the result sporadic.
 * There is one contact model now, and its answer rides on the row it describes.
 */

/**
 * Telemetry: what the system knows about its own coverage.
 *
 * Replaces the Tracking panel from his mock. Every figure is counted. A null reads
 * "never", because "never polled" and "polled and found nothing" are different claims
 * and a dashboard that conflates them is lying quietly.
 */
export function Telemetry({
  telemetry,
  lanes,
  holdings,
}: {
  telemetry: DashboardTelemetry;
  lanes: DashboardLanes;
  holdings: DashboardHoldings;
}): JSX.Element {
  const conflicts = telemetry.attentionByKind['unresolved-identity'] ?? 0;
  const cells: { label: string; value: string; hint?: string }[] = [
    { label: 'Notes', value: String(holdings.totalNotes) },
    { label: 'Actionable', value: String(lanes.due + lanes.live), hint: `${lanes.archive} archived` },
    { label: 'Meetings', value: String(telemetry.meetings) },
    { label: 'Transcribed', value: String(telemetry.meetingsTranscribed) },
    { label: 'Conflicts', value: String(conflicts) },
    { label: 'Calendar', value: sinceLabel(telemetry.calendarLastSeen) },
    { label: 'Mail', value: sinceLabel(telemetry.mailLastRun) },
  ];

  /*
   * Hermes cron, as a dead-man's switch.
   *
   * Appended rather than inlined above so the three states read differently, which is the whole
   * point: `late` is the alarm, `never` is "not wired up yet" and must NOT render as a failure, and
   * an older server omitting the field renders nothing at all. Cron ran nothing for two days in
   * August 2026 behind a valid-looking config, and nothing on any screen said so.
   */
  const cron = telemetry.hermesCron;
  if (cron !== undefined && cron.state !== 'never') {
    cells.push({
      label: 'Hermes cron',
      value: cron.state === 'late' ? 'LATE' : sinceLabel(new Date(Date.now() - (cron.minutesAgo ?? 0) * 60_000).toISOString()),
      hint:
        cron.state === 'late'
          ? `no beat in ${cron.minutesAgo}m, expected every ${cron.everyMinutes}m`
          : `every ${cron.everyMinutes}m`,
    });
  }
  return (
    <section className="deck-telemetry" aria-label="System telemetry">
      {cells.map((c) => (
        <div key={c.label} className="deck-tel">
          <span className="deck-tel-label">{c.label}</span>
          <span className="deck-tel-value">{c.value}</span>
          {c.hint !== undefined && <span className="deck-tel-hint">{c.hint}</span>}
        </div>
      ))}
    </section>
  );
}

/** "never" rather than a zero, and a coarse age rather than a false precision. */
function sinceLabel(iso: string | null): string {
  if (iso === null || iso === '') return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (mins < 2) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  return hours < 48 ? `${hours}h` : `${Math.round(hours / 24)}d`;
}

export function KeyActionList({ items }: { items: DashboardKeyAction[] }): JSX.Element {
  const reveal = useReveal();
  if (items.length === 0) {
    return <p className="mo-empty">Nothing is actionable right now.</p>;
  }
  return (
    <ul className="deck-actions">
      {items.map((a) => (
        <li key={a.path} className="deck-action" data-lane={a.lane}>
          <button type="button" className="deck-action-btn" onClick={() => reveal(a.path)}>
            <span className="deck-action-text">{a.text}</span>
            <span className="deck-action-meta">
              <span className={a.mine ? 'deck-tag deck-tag-mine' : 'deck-tag'}>
                <Icon name={a.mine ? 'owed' : 'waiting'} size={13} />
                {a.mine ? 'you owe' : 'waiting'}
              </span>
              <span className="deck-reason">{a.reason}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/*
 * `GraphPanel` lived here: a ring of the nine most active organisations, sized by engagements and
 * people, drawn from `holdings.activeOrgs`.
 *
 * DROPPED AT HIS DECISION, 2026-08-09, along with the node-mix donut and the holdings block. It
 * was accurate and it was pretty, and it never once told him to do anything: an org being large in
 * the graph is a fact about how much mail that account generates, not about whether it needs him.
 * The force layout also draws every node, which is the first thing that degrades as the vault
 * grows, so this was going to need clustering work to keep a view nobody acted on.
 *
 * The real graph is still in the vault and reachable from the vault link in the footer. If a graph
 * comes back to the deck, it should answer a question first: WHICH accounts have one point of
 * contact, or which have gone quiet, not which are biggest.
 */
/**
 * Agents. Hermes is real and connected over MCP; the rest are his personal-company
 * architecture and do not exist, so they are greyed rather than shown as "Active".
 */
/**
 * The next meeting, and the rest of the run under it.
 *
 * Every row carries its own Prepare. That is the point of the card rather than a
 * detail of it: the meeting he most needs to think about is often the third one,
 * and offering the pre-read only on the nearest makes the other two his problem to
 * remember. A recurring series still gets no offer, for the reason AgendaList
 * already gives: a standup is not worth a pre-read.
 *
 * Renders nothing when the calendar is empty rather than an empty card, so an
 * unwired or quiet calendar costs no height.
 */
/**
 * Commitments you owe the person you are about to sit down with.
 *
 * THIS IS THE HONEST VERSION OF "3 COMMITMENTS LINKED". His reference board showed a readiness
 * block on this card and it is the right idea, but the payload carries no link from a meeting to a
 * commitment: `DashboardCommitment` has a counterparty and a topic, and `DashboardMeeting` has an
 * organiser. Inventing a link, or a "brief ready" flag with nothing behind it, would put a
 * confident number on the one card he opens under time pressure.
 *
 * So this matches on the counterparty, which is a real relation and arguably the more useful one:
 * before a meeting with someone, what do you already owe them. Matching is loose on case and
 * whitespace only, never on substrings, because "Ada" inside "Adam Sorensen" is exactly the kind
 * of near-match that produces a wrong promise read out in a meeting.
 */
function owedTo(who: string, owed: DashboardCommitment[]): DashboardCommitment[] {
  const key = who.trim().toLowerCase();
  if (key === '') return [];
  return owed.filter((c) => c.counterparty.trim().toLowerCase() === key);
}

export function NextMeeting({
  meetings,
  owed = [],
  onPrepare,
}: {
  meetings: DashboardMeeting[];
  /** His open commitments, so the card can say what he owes whoever called the meeting. */
  owed?: DashboardCommitment[];
  /** Brief me on this one. Absent on surfaces with no chat, where the card stays a readout. */
  onPrepare?: (meeting: DashboardMeeting) => void;
}): JSX.Element | null {
  const reveal = useReveal();
  if (meetings.length === 0) return null;
  const [next, ...rest] = meetings;
  const due = owedTo(next.organiser, owed);
  const overdue = due.filter((c) => c.dueInDays !== null && c.dueInDays < 0).length;
  return (
    <section className="deck-panel deck-next" data-hue="time">
      <header className="deck-panel-head">
        <Icon name="agenda" size={16} className="deck-panel-icon" />
        <h2 className="deck-panel-title">Next meeting</h2>
        {rest.length > 0 && <span className="deck-count">{meetings.length}</span>}
      </header>

      <p className="deck-next-when">{meetingWhen(next)}</p>
      <button type="button" className="deck-next-title" onClick={() => reveal(next.title)}>
        {next.title}
      </button>
      <p className="deck-next-who">
        {next.organiser !== '' ? next.organiser : 'No organiser recorded'}
        {next.recurring && ' · repeats'}
      </p>

      {/*
        THE LAUNCHPAD. The card was a readout: it told him a meeting was coming and left him to
        work out what to do about it. These two lines are the only readiness facts the payload
        actually supports, and Prepare is the act the card exists to start.
      */}
      {(due.length > 0 || onPrepare !== undefined) && (
        <div className="deck-next-ready">
          {due.length > 0 && (
            <button
              type="button"
              className="deck-next-fact"
              data-tone={overdue > 0 ? 'crit' : undefined}
              onClick={() => reveal(due[0]!.path)}
            >
              <Icon name="owed" size={13} />
              {due.length === 1 ? '1 promise' : `${due.length} promises`} to {next.organiser}
              {overdue > 0 && <span className="deck-next-flag">{overdue} overdue</span>}
            </button>
          )}

          {onPrepare !== undefined && (
            <button type="button" className="deck-next-prep" onClick={() => onPrepare(next)}>
              <Icon name="spark" size={13} />
              Prepare
            </button>
          )}
        </div>
      )}
      {!next.recurring && <PrepareButton title={next.title} />}

      {rest.length > 0 && (
        <div className="deck-next-rest">
          <p className="deck-next-rest-head">
            Also coming up
            <span>{rest.length}</span>
          </p>
          <ul className="deck-next-list">
            {rest.map((m) => (
              <li key={m.path} className="deck-next-item">
                <span className="deck-next-item-when">{meetingWhen(m)}</span>
                <span>
                  <button
                    type="button"
                    className="deck-next-item-title"
                    onClick={() => reveal(m.title)}
                  >
                    {m.title}
                  </button>
                  <span className="deck-next-item-who">
                    {m.organiser !== '' ? m.organiser : 'no organiser recorded'}
                    {m.recurring && ' · repeats'}
                  </span>
                </span>
                {!m.recurring && <PrepareButton title={m.title} />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * The system light in the header corner.
 *
 * Green means every ingestion lane reported cleanly on the last run; amber means at
 * least one limitation is on record, and it says how many rather than just changing
 * colour, because "amber" alone would send him hunting for what.
 *
 * Deliberately reads the SAME list the footer prints in full. A second source would
 * let the light and the explanation disagree, and this is the one element whose job
 * is to say whether the rest of the board can be trusted.
 */
export function SystemState({ limitations }: { limitations: string[] }): JSX.Element {
  const n = limitations.length;
  const ok = n === 0;
  return (
    <p className="deck-sysstate" data-ok={ok}>
      <span className="deck-sysstate-line">
        <span className="deck-sysstate-dot" aria-hidden="true" />
        {ok ? 'System nominal' : `${n} known gap${n === 1 ? '' : 's'}`}
      </span>
      <span className="deck-sysstate-note">
        {ok ? 'every lane reported cleanly' : 'listed at the foot of the board'}
      </span>
    </p>
  );
}

/**
 * The hero band: the wordmark, the sentinel, and the three numbers that decide
 * whether he reads any further.
 *
 * The three are COUNTED from the payload rather than written in, for the same
 * reason the rail captions are: a band that says "3 decisions" while the panel
 * below it lists seven is worse than no band, because it is the part he reads
 * first and the part he would stop trusting first.
 *
 * The next-meeting slot deliberately shows the next meeting he could PREPARE for,
 * skipping recurring series. A standup is not worth a pre-read, and putting one
 * here would make the band say "next up: standup" every morning regardless of
 * what the day actually holds. When there is no such meeting it says so rather
 * than falling back to a recurring one.
 */
function Hero({
  dashboard: d,
  onPick,
}: {
  dashboard: Dashboard;
  onPick: (t: string) => void;
}): JSX.Element {
  const decisions = d.proposals.length + d.proposalsMore;
  const actions = d.keyActions.length + d.keyActionsMore;
  const next = d.agenda.find((m) => !m.recurring) ?? null;

  return (
    <section className="deck-hero" aria-label="MO OS">
      <img className="deck-hero-vista" src="/deck-vista.webp" alt="" aria-hidden="true" />

      <div>
        <p className="deck-hero-word">MO OS</p>
        <p className="deck-hero-sub">Your personal operating system</p>
        <p className="deck-hero-say">
          I monitor, connect and remind.
          <br />
          You decide, I execute.
        </p>
      </div>

      <img
        className="deck-hero-figure"
        src="/deck-mo.webp"
        alt=""
        width={460}
        height={460}
        aria-hidden="true"
      />

      <div className="deck-hero-stats">
        <button
          type="button"
          className="deck-hero-stat"
          data-tone="judgement"
          onClick={() => onPick('')}
        >
          <span className="deck-hero-ring">
            <Icon name="judgement" size={16} />
          </span>
          <span>
            <span className="deck-hero-value">{decisions}</span>
            <span className="deck-hero-label">{decisions === 1 ? 'Decision' : 'Decisions'}</span>
            <span className="deck-hero-note">need your judgement</span>
          </span>
        </button>

        <button
          type="button"
          className="deck-hero-stat"
          data-tone="action"
          onClick={() => onPick('commitment')}
        >
          <span className="deck-hero-ring">
            <Icon name="action" size={16} />
          </span>
          <span>
            <span className="deck-hero-value">{actions}</span>
            <span className="deck-hero-label">{actions === 1 ? 'Action' : 'Actions'}</span>
            <span className="deck-hero-note">need your attention</span>
          </span>
        </button>

        <button
          type="button"
          className="deck-hero-stat"
          data-tone="time"
          onClick={() => onPick(next?.title ?? 'meeting')}
        >
          <span className="deck-hero-ring">
            <Icon name="clock" size={16} />
          </span>
          <span>
            <span className="deck-hero-value">
              {next === null ? 'Nothing to prepare' : `Next up ${meetingWhen(next)}`}
            </span>
            <span className="deck-hero-label">{next?.title ?? 'No one-off meeting ahead'}</span>
            <span className="deck-hero-note">
              {next === null
                ? 'recurring series are not counted here'
                : next.organiser !== ''
                  ? next.organiser
                  : 'no organiser recorded'}
            </span>
          </span>
        </button>
      </div>
    </section>
  );
}

/**
 * The crew.
 *
 * Two changes from the list this replaces, and both are corrections rather than
 * decoration. Zou was missing entirely although it is built and running, so the
 * panel under-reported what is acting for him. And "not built" and "planned" were
 * collapsed into one word, which made three quite different states look alike: the
 * CFO agent has no design, Research and Marketing have one and no code.
 *
 * The portraits are the deck's own crew plates. They carry alpha, so they need no
 * blend mode and sit correctly on either skin; the unbuilt three are rendered dark
 * and visorless in the art itself rather than dimmed in CSS, so the state survives
 * a screenshot.
 */
/*
 * FALLBACK ONLY. The roster is the server's (`telemetry.crew`, see mo/crew.ts). This const was the
 * roster, and its `live: true` was a claim: it showed Hermes active on every one of the nine days
 * its scheduler was dead. When an older server sends no crew, the honest answer about every agent
 * is that we do not know, so nothing here claims to be running.
 */
const CREW_FALLBACK: CrewRow[] = [
  { name: 'Fawzy', role: 'Capture & automate', state: 'planned', plate: 'hermes', detail: 'state not reported' },
  { name: 'Zou', role: 'Create & compose', state: 'planned', plate: 'zou', detail: 'state not reported', mark: '象' },
  { name: 'Hibiki', role: 'Brand & content', state: 'planned', plate: 'hibiki', detail: 'state not reported', mark: '響' },
  { name: 'Kantoku', role: 'Personal brand', state: 'planned', plate: 'kantoku', detail: 'state not reported', mark: '監督' },
  { name: 'Satori', role: 'External intel', state: 'planned', plate: 'research', detail: 'state not reported', mark: '悟' },
  { name: 'Kura', role: 'Financial intel', state: 'planned', plate: 'cfo', detail: 'state not reported', mark: '蔵' },
];

export function AgentsPanel({ crew }: { crew?: CrewRow[] } = {}): JSX.Element {
  const rows = crew !== undefined && crew.length > 0 ? crew : CREW_FALLBACK;
  const live = rows.filter((a) => isLive(a.state)).length;
  const lateCount = rows.filter((a) => a.state === 'late').length;
  return (
    <section className="deck-panel" data-hue="health">
      <header className="deck-panel-head">
        <Icon name="agent" size={16} className="deck-panel-icon" />
        <h2 className="deck-panel-title">The crew</h2>
        {/* A silent agent is the headline. See the deck's copy of this panel. */}
        <span className="deck-count" data-alarm={lateCount > 0}>
          {lateCount > 0 ? `${lateCount} silent` : live}
        </span>
      </header>
      <ul className="deck-crew">
        {rows.map((a) => (
          <li
            key={a.name}
            className="deck-crew-member"
            data-live={isLive(a.state)}
            data-state={a.state}
            title={a.detail}
          >
            <span className="deck-crew-plate">
              <img src={`/crew-${a.plate}.webp`} alt="" width={216} height={162} loading="lazy" />
            </span>
            <span className="deck-crew-name">
              {a.name}
              {/* The kanji as a quiet mark, not a decoration: it is the name, in its own script. */}
              {a.mark !== undefined && <span className="deck-crew-mark" aria-hidden="true">{a.mark}</span>}
            </span>
            <span className="deck-crew-role">{a.role}</span>
            <span className="deck-crew-state">
              <span className="deck-crew-dot" aria-hidden="true" />
              {a.state === 'late' ? a.detail : a.state}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Ask and discover. There is no retrieval endpoint on /api/mo and the assistant
 * re-point was deferred (A5-2), so this drives the vault search index that
 * already exists rather than implying a conversation this surface cannot hold.
 */
/**
 * The search field.
 *
 * `label` and `placeholder` are overridable because the two dashboard shapes name this the way
 * their own mockups do: the tower says "Ask or discover / Search everything MO OS knows", the
 * Command Deck says "Ask MO OS anything". Same field, same behaviour, so it stays one component
 * rather than two that would drift on what submitting does.
 */
export function AskField({
  label = 'Ask or discover',
  placeholder = 'Search everything MO OS knows',
}: {
  label?: string;
  placeholder?: string;
} = {}): JSX.Element {
  const [q, setQ] = useState('');
  const setSearchQuery = usePaneState((s) => s.setSearchQuery);
  const setSidebarTab = useApp((s) => s.setSidebarTab);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const query = q.trim();
    if (query === '') return;
    setSearchQuery(query);
    setSidebarTab('search');
  };

  return (
    <form className="mo-ask" onSubmit={submit} role="search">
      <label className="mo-ask-label" htmlFor="mo-ask-field">
        {label}
      </label>
      <div className="mo-ask-row">
        <input
          id="mo-ask-field"
          className="mo-ask-field"
          type="search"
          value={q}
          placeholder={placeholder}
          onChange={(e) => setQ(e.target.value)}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
        />
        <button type="submit" className="mo-ask-go">
          Find
        </button>
      </div>
    </form>
  );
}

export function IntentBlock({
  objectives,
  onSaved,
}: {
  objectives: DashboardObjective[];
  onSaved: () => void;
}): JSX.Element {
  // Open by default when nothing is declared. The empty state is otherwise a dead
  // end: it correctly explains that gaps cannot be computed, and then offers no way
  // to fix that.
  const [declaring, setDeclaring] = useState(objectives.length === 0);

  if (objectives.length === 0) {
    return (
      <section className="mo-dash-intent mo-dash-intent-empty">
        <h2 className="mo-dash-h">What I want</h2>
        <p className="mo-dash-lede">No objectives are declared.</p>
        <p className="mo-dash-note">
          Focus areas and gaps cannot be computed without them. A gap is declared intent minus
          observed reality, and with nothing declared there is nothing to measure the vault
          against. Objectives are authored, never distilled from correspondence.
        </p>
        <ObjectiveForm onSaved={onSaved} />
      </section>
    );
  }

  return (
    <section className="mo-dash-intent">
      <h2 className="mo-dash-h">What I want</h2>
      {declaring ? (
        <ObjectiveForm
          onSaved={() => {
            setDeclaring(false);
            onSaved();
          }}
          onCancel={() => setDeclaring(false)}
        />
      ) : (
        <button type="button" className="mo-declare-toggle" onClick={() => setDeclaring(true)}>
          Declare another objective
        </button>
      )}
      <ul className="mo-objectives">
        {objectives.map((o) => (
          <li key={o.path} className="mo-objective">
            <p className="mo-objective-name">{o.name}</p>
            {o.summary !== '' && <p className="mo-objective-summary">{o.summary}</p>}
            <p className="mo-meta">
              {o.status !== '' && <span>{o.status}</span>}
              {o.horizon !== '' && <span>horizon {o.horizon}</span>}
              <span>
                {o.openCommitments} open {o.openCommitments === 1 ? 'commitment' : 'commitments'}
              </span>
              <span className="mo-stale" data-stale={o.staleDays === null || o.staleDays > 21}>
                {staleLabel(o.staleDays)}
              </span>
            </p>
            {o.engagements.length > 0 && (
              <p className="mo-meta">served by {o.engagements.join(', ')}</p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Nothing linked at all is a worse signal than a stale link, so it is named. */
function staleLabel(staleDays: number | null): string {
  if (staleDays === null) return 'nothing is linked to this';
  if (staleDays === 0) return 'moved today';
  return `${staleDays} ${staleDays === 1 ? 'day' : 'days'} since anything moved`;
}

/**
 * Gaps are the whole reason the intent layer exists, so they get their own block
 * rather than a footnote under the objectives that produced them.
 */
export function GapsBlock({ gaps }: { gaps: DashboardGap[] }): JSX.Element | null {
  if (gaps.length === 0) return null;
  return (
    <section className="mo-gaps">
      <h2 className="mo-dash-h">Gaps</h2>
      <ul className="mo-gap-list">
        {gaps.map((g) => (
          <li key={`${g.kind}:${g.ref}:${g.title}`} className="mo-gap">
            <p className="mo-gap-title">{g.title}</p>
            <p className="mo-gap-detail">{g.detail}</p>
            <p className="mo-meta">{g.kind}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ProposalsBlock({
  proposals,
  more,
  onReject,
  onAccepted,
}: {
  proposals: DashboardProposal[];
  /** Open questions NOT shown. Stated, never silent: see the note in queries.ts. */
  more: number;
  onReject: (id: number) => void;
  onAccepted: (id: number) => void;
}): JSX.Element | null {
  // Which proposal is being authored, if any. One at a time: answering two at once
  // invites pasting the same objective twice under different names.
  const [accepting, setAccepting] = useState<DashboardProposal | null>(null);
  // Which identity question is open in the merge panel. Separate from `accepting`
  // because they answer different kinds and must not share a slot: opening one should
  // not silently close a half-filled form of the other.
  const [merging, setMerging] = useState<DashboardProposal | null>(null);
  const reveal = useReveal();

  if (proposals.length === 0) return null;
  return (
    <section className="deck-panel mo-proposals" data-hue="ask">
      <header className="deck-panel-head">
        <Icon name="judgement" size={16} className="deck-panel-icon" />
        <h2 className="deck-panel-title">Needs your judgement</h2>
        <span className="deck-count">{proposals.length}</span>
      </header>
      {/*
        SHORTENED AND FOLDED, 2026-08-09, when this panel moved into Tier 1.

        It carried four lines of body-sized prose explaining how the controls differ by kind. That
        was fine in the far-right column where it used to live. In Decide it pushed "What I need to
        do" and "Waiting on others" below the fold on a 1440px screen, so an explanation of the
        panel was outranking the two lists the column exists for. The operative sentence stays
        visible because it has a consequence he needs to expect; the rest is one click away and is
        the kind of thing you read once.
      */}
      <p className="deck-panel-note">
        Answering clears the item in the shared attention feed, so it also clears in Telegram.
        {more > 0 && ` ${more} more are waiting in the attention feed.`}
      </p>
      <details className="mo-proposals-help">
        <summary>How the controls differ</summary>
        <p className="deck-panel-note">
          One question each, and the controls depend on which question it is. An identity conflict
          asks whether two records are the same person; an objective proposal asks you to declare
          intent in your own words.
        </p>
      </details>
      <ul className="mo-proposal-list">
        {proposals.map((p) => (
          <li key={p.id} className="mo-proposal">
            <p className="mo-proposal-title">{p.title}</p>
            <p className="mo-meta">
              <span>{p.kind}</span>
              {p.ref !== '' && <span>{p.ref}</span>}
              <span>{whenLabel(p.createdAt)}</span>
            </p>
            {/*
              KIND-AWARE, and he reported twice why it had to be: first that Accept was
              faded out, then that the panel was still wrong. The second was the real
              bug. PROPOSAL_KINDS includes unresolved-identity, and the live feed holds
              196 of those against ZERO objective proposals, so every control he ever
              saw was answering a question nobody asked.

              An identity conflict asks: same person, or two people? Merging is a real
              write and is not built yet, so this says so rather than showing a dead
              button. "Different people" is an answer he can already give, and it
              resolves the item so it stops being asked.

              Accept still opens the authoring form for a genuine objective proposal,
              because per A5-3 intent has to be declared in his own words: mapping a
              proposal's one sentence onto an objective name would author a junk
              objective and skew every focus and gap calculation below it.
            */}
            {p.kind === 'unresolved-identity' ? (
              <>
                <p className="mo-proposal-actions">
                  {/*
                    Merge is only offered for the refs it can actually answer. An identity
                    item comes from two places: `detectIdentityConflicts`, whose ref is
                    `mo:identity:<email>` and names the notes to reconcile, and the
                    pipeline's "Which X is this?", whose ref is a person path and carries
                    no second side. Offering a merge button on the latter would open a
                    panel with nothing to merge.
                  */}
                  {p.ref.startsWith('mo:identity:') ? (
                    <button
                      type="button"
                      className="mo-btn mo-btn-primary"
                      aria-expanded={merging?.id === p.id}
                      onClick={() => setMerging(merging?.id === p.id ? null : p)}
                    >
                      {merging?.id === p.id ? 'Cancel' : 'Same person, merge them'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="mo-btn"
                      onClick={() => reveal(p.ref === '' ? p.title : p.ref)}
                    >
                      Look at the records
                    </button>
                  )}
                  <button type="button" className="mo-btn" onClick={() => onReject(p.id)}>
                    Different people
                  </button>
                </p>
                {merging?.id === p.id && (
                  <MergePanel
                    ref_={p.ref}
                    onCancel={() => setMerging(null)}
                    onMerged={() => {
                      setMerging(null);
                      // Resolve only after the write landed, so a failed merge leaves the
                      // question standing rather than quietly clearing it.
                      onAccepted(p.id);
                    }}
                  />
                )}
              </>
            ) : (
              <p className="mo-proposal-actions">
                <button
                  type="button"
                  className="mo-btn mo-btn-primary"
                  aria-expanded={accepting?.id === p.id}
                  onClick={() => setAccepting(accepting?.id === p.id ? null : p)}
                >
                  {accepting?.id === p.id ? 'Cancel' : 'Accept'}
                </button>
                <button type="button" className="mo-btn" onClick={() => onReject(p.id)}>
                  Reject
                </button>
              </p>
            )}
            {accepting?.id === p.id && (
              <ObjectiveForm
                answering={p.title}
                initialSummary={p.title}
                onCancel={() => setAccepting(null)}
                onSaved={() => {
                  setAccepting(null);
                  // Only now, once the objective exists.
                  onAccepted(p.id);
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * A shrinking list: a few rows, then "show all N".
 *
 * This is what "shrinking lists" had to mean. Collapsing the whole panel hides the
 * content and makes the deck look emptier; capping the rows keeps every panel a
 * glanceable height while leaving nothing unreachable. 20 owed commitments in one
 * column is the flat wall again, just narrower.
 */
const LIST_CAP = 4;

export function CommitmentList({
  items,
  empty,
  onClosed,
  total,
  lane,
}: {
  items: DashboardCommitment[];
  empty: string;
  /** Refetch after a close, so the row and every count move together. */
  onClosed?: () => void;
  /**
   * The REAL count. `items` is the twelve the payload carries, so this is what the expand
   * button must name and what it fetches.
   */
  total?: number;
  lane?: 'owed' | 'waiting';
}): JSX.Element {
  const [all, setAll] = useState(false);
  /** The rest of the lane, once he asks for it. */
  const [full, setFull] = useState<DashboardCommitment[] | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);
  /** The bulk review overlay. Open only once the whole lane is in hand. */
  const [reviewing, setReviewing] = useState(false);
  /** Closed optimistically: the row goes at once, and comes back if the write failed. */
  const [closing, setClosing] = useState<Record<string, true>>({});
  const [problem, setProblem] = useState('');

  const close = async (path: string): Promise<void> => {
    setProblem('');
    setClosing((m) => ({ ...m, [path]: true }));
    const r = await closeCommitment(path);
    if (r.ok) {
      onClosed?.();
      return;
    }
    // Put it back. A row that vanished on a failed write would tell him it was done.
    setClosing((m) => {
      const { [path]: _gone, ...rest } = m;
      return rest;
    });
    setProblem(r.error);
  };

  /**
   * Expand: fetch the rest first, so the button never claims to show more than it has.
   *
   * The payload carries twelve per lane. Expanding in place would show twelve under a label
   * saying 519, which is a worse failure than not offering the button.
   */
  const expand = async (): Promise<void> => {
    if (all) {
      setAll(false);
      return;
    }
    if (full === null && lane !== undefined && (total ?? 0) > items.length) {
      setLoadingFull(true);
      const r = await fetchLane(lane);
      setLoadingFull(false);
      if (r.ok) setFull(r.items);
      else {
        setProblem(r.error);
        return;
      }
    }
    setAll(true);
  };

  /**
   * Open the bulk review over the WHOLE lane.
   *
   * The lane is fetched first and the overlay only opens on success. A review screen that
   * opened over the twelve rows the payload carries would let him "clear the backlog" and
   * leave 506 of them untouched, which is worse than not offering the button: he would come
   * away believing the list was empty.
   */
  const review = async (): Promise<void> => {
    setProblem('');
    if (lane === undefined) return;
    if (full === null && (total ?? 0) > items.length) {
      setLoadingFull(true);
      const r = await fetchLane(lane);
      setLoadingFull(false);
      if (!r.ok) {
        setProblem(r.error);
        return;
      }
      setFull(r.items);
    }
    setReviewing(true);
  };

  if (items.length === 0) return <p className="mo-dash-note">{empty}</p>;
  const source = all && full !== null ? full : items;
  const live = source.filter((c) => closing[c.path] !== true);
  const shown = all ? live : live.slice(0, LIST_CAP);
  return (
    <>
    {problem !== '' && <p className="mo-social-problem">{problem}</p>}
    <ul className="mo-commitments">
      {shown.map((c) => {
        const overdue = c.dueInDays !== null && c.dueInDays < 0;
        return (
          <li key={c.path} className="mo-commitment" data-overdue={overdue}>
            <p className="mo-commitment-text">{c.text}</p>
            <p className="mo-meta">
              {c.counterparty !== '' && <span className="mo-who">{c.counterparty}</span>}
              <span className="mo-due" data-overdue={overdue}>
                {dueInDaysLabel(c.dueInDays, c.due)}
              </span>
              {c.blockedOn !== '' && <span>blocked on {c.blockedOn}</span>}
              {c.topic !== '' && <span>{c.topic}</span>}
              {c.objective !== '' && <span className="mo-serves">serves {c.objective}</span>}
              {/*
                One click, no confirm. Closing is reversible (the note keeps everything and
                only `status` flips) and it is the action he will use most, so a confirm step
                would make clearing 519 items unbearable. Contrast the social publish button,
                which is irreversible and therefore takes two.
              */}
              <button
                type="button"
                className="mo-commitment-done"
                title={`Mark done: ${c.text}`}
                aria-label={`Mark done: ${c.text}`}
                onClick={() => void close(c.path)}
              >
                Done
              </button>
            </p>
          </li>
        );
      })}
    </ul>
    {(total ?? live.length) > LIST_CAP && (
      <div className="mo-commitment-actions">
        <button type="button" className="deck-more" disabled={loadingFull} onClick={() => void expand()}>
          {loadingFull ? 'Loading…' : all ? 'Show fewer' : `Show all ${total ?? live.length}`}
        </button>
        {/*
          Separate from "Show all" on purpose. Expanding is for reading a few more rows in
          place; reviewing is for clearing a backlog of hundreds, and it needs the whole
          screen. One button that did both would be the wrong size for each.
        */}
        {lane !== undefined && (
          <button type="button" className="deck-more" disabled={loadingFull} onClick={() => void review()}>
            Review and clear
          </button>
        )}
      </div>
    )}
    {reviewing && lane !== undefined && (
      <CommitmentReview
        lane={lane}
        items={full ?? source}
        onClose={() => setReviewing(false)}
        onChanged={() => {
          // Refetch the dashboard so every count moves together, and drop the cached lane so
          // reopening the review does not show rows that are already closed.
          setFull(null);
          onClosed?.();
        }}
      />
    )}
    </>
  );
}

/**
 * What is actually on his calendar next.
 *
 * The panel was called Today and showed recently-CHANGED notes, which answers "what moved" and
 * not "what is my day". The calendar has been ingesting since 2026-08-03 and nothing on the
 * landing view used it, so the question a dashboard is most expected to answer was the one it
 * could not.
 *
 * Reaches a week forward rather than strictly today, because his live vault had ZERO meetings
 * dated today and one at 13:00 tomorrow. A panel titled Today that is empty on such a day is
 * technically correct and practically useless, so it labels which are today instead of hiding
 * the rest.
 */
export function AgendaList({ meetings }: { meetings: DashboardMeeting[] }): JSX.Element | null {
  const reveal = useReveal();
  if (meetings.length === 0) return null;
  return (
    <ul className="mo-agenda">
      {meetings.map((m) => (
        <li key={m.path} className="mo-agenda-item" data-today={m.isToday}>
          <button type="button" className="mo-agenda-btn" onClick={() => reveal(m.title)}>
            <span className="mo-agenda-when">{meetingWhen(m)}</span>
            <span className="mo-agenda-title">{m.title}</span>
            <span className="mo-agenda-meta">
              {m.organiser !== '' && <span>{m.organiser}</span>}
              {m.recurring && <span className="mo-agenda-repeat">repeats</span>}
            </span>
          </button>
          {/*
            PREPARE, offered rather than waited for.
            
            His words: "Platform should tell me You have this important meeting X tomorrow, Prepare
            Pre-reads". He should not have to remember that Zou4Mo exists, work out what the meeting
            is called, and type it. A cancelled meeting is excluded upstream, and a recurring
            standup is not worth a pre-read, so the offer is only on the ones that earn it.
          */}
          {!m.recurring && <PrepareButton title={m.title} />}
        </li>
      ))}
    </ul>
  );
}

/**
 * One click from "I have a meeting" to a briefing that names what a win would be.
 *
 * It opens the ask overlay with the meeting already named and the pre-meeting briefing already
 * selected, because the step this removes is exactly the one he should not have to do: knowing
 * that the feature exists and what to type into it.
 */
export function PrepareButton({ title }: { title: string }): JSX.Element {
  return (
    <button
      type="button"
      className="mo-agenda-prep"
      onClick={() => openAsk({ lane: 'zou', kind: 'pre-meeting-briefing', about: title })}
      title={`Prepare a briefing for ${title}`}
    >
      Prepare
    </button>
  );
}

/**
 * When a meeting is, in words.
 *
 * Graph writes `2026-08-06T13:00:00.0000000`: seven fractional digits, which `Date.parse` rejects,
 * and no timezone suffix at all.
 *
 * The old version therefore sliced the string and printed the raw digits, which meant a 13:00
 * meeting read as 13:00 whatever zone he was in. That is UTC wearing no label, and it is the same
 * bug as the header clock had.
 *
 * Converting is safe here because the source zone is known: `calendar.ts` stores `time_zone`
 * alongside, and on his live vault every meeting reads `time_zone: UTC` (checked, all 7). So the
 * value is normalised to a real instant and rendered in HIS zone.
 *
 * If a meeting ever arrives in another zone, this becomes wrong by that offset. Graph reports
 * Windows zone names rather than IANA ones, so translating them properly needs a mapping table on
 * the server; `docs/findings` records that. Until then the conversion is correct for every meeting
 * actually in the vault, which beats printing UTC and calling it local.
 */
export function meetingWhen(m: DashboardMeeting): string {
  const t = utcInstant(m.startsAt);
  if (m.allDay) {
    return m.isToday ? 'all day' : `${m.startsAt.slice(0, 10)}, all day`;
  }
  if (t === null) {
    // No usable time: say the day rather than inventing an hour.
    return m.isToday ? 'today' : m.startsAt.slice(0, 10);
  }
  const d = new Date(t);
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  if (m.isToday) return time;
  const day = d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  return `${day} ${time}`;
}

/**
 * A timezone-naive Graph timestamp, read as the UTC instant it actually is.
 *
 * Two fixes in one: the fractional part is truncated to milliseconds, because `Date.parse` rejects
 * seven digits, and a `Z` is appended when there is no offset, because otherwise the browser would
 * read it as LOCAL time and a UTC value would shift by his own offset.
 */
function utcInstant(raw: string): number | null {
  if (raw === '') return null;
  const trimmed = raw.replace(/(\.\d{3})\d+$/, '$1');
  const withZone = /(Z|[+-]\d{2}:?\d{2})$/.test(trimmed) ? trimmed : `${trimmed}Z`;
  const t = Date.parse(withZone);
  return Number.isNaN(t) ? null : t;
}

export function UpdateList({ updates }: { updates: DashboardUpdate[] }): JSX.Element {
  if (updates.length === 0) return <p className="mo-dash-note">Nothing has changed.</p>;
  return (
    <ul className="mo-updates">
      {updates.map((u) => (
        <li key={`${u.path}:${u.at}`} className="mo-update">
          <span className="mo-update-kind">{u.kind}</span>
          <span className="mo-update-title">{u.title}</span>
          <span className="mo-update-when">{whenLabel(u.at)}</span>
        </li>
      ))}
    </ul>
  );
}

/*
 * `HoldingsBlock` lived here: total notes, a count per node kind, and every active org. Dropped
 * with the graph and the donut for the same reason. See the note where GraphPanel was.
 */
/**
 * Rendered verbatim and unconditionally. A tower that implies it can see his
 * calendar when it cannot is worse than one that says so, and the empty case is
 * the one where that would silently happen.
 */
export function LimitationsBlock({ limitations }: { limitations: string[] }): JSX.Element {
  return (
    <section className="mo-dash-limits">
      <h2 className="mo-dash-h">What this does not know</h2>
      {limitations.length === 0 ? (
        <p className="mo-dash-note">The server listed no limitations for this view.</p>
      ) : (
        <ul>
          {limitations.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The notes app is the second layer now, so it needs a way back that is not the URL bar. */
export function VaultLink(): JSX.Element {
  const openView = useApp((s) => s.openView);
  return (
    <p className="mo-dash-foot">
      <button type="button" className="mo-btn" onClick={() => openView({ kind: 'empty' })}>
        Open the vault
      </button>
      <button type="button" className="mo-btn" onClick={() => openView({ kind: 'mo' })}>
        Open the attention feed
      </button>
    </p>
  );
}

/** Timestamps that are unparseable are shown as sent rather than as "Invalid Date". */
export function whenLabel(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  /*
   * HIS clock, not the server's.
   *
   * This used to call `toISOString()`, which is always UTC, so the header read 23:25 while his Mac
   * said 19:25 and he saw times in the small hours that meant nothing to him. Formatting in the
   * browser fixes it properly rather than by configuration: `toLocaleString` uses whatever zone the
   * machine is in, so it follows him when he travels and needs no setting to keep in sync.
   *
   * `undefined` for the locale on purpose, so day and month order follow his system too.
   */
  return new Date(t).toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
